/**
 * Writing-day streak — pure logic, no DB (unit-tested).
 *
 * Rules:
 *   · The current streak counts CONSECUTIVE days with at least one entry,
 *     ending today or yesterday: not writing today yet does NOT break the streak
 *     (the day is not over), but it does not increment it either.
 *   · The longest streak is the longest consecutive sequence in the history.
 */

import { addDaysToKey, diffInDays } from "./dates";

export interface Streaks {
  /** Consecutive days writing through today/yesterday. */
  current: number;
  /** Longest historical streak. */
  longest: number;
  /** Is there already an entry today? (for the "keep it up!" vs "write today" copy). */
  wroteToday: boolean;
}

/**
 * Computes streaks from the days with entries (YYYY-MM-DD keys,
 * duplicates and order irrelevant) and today's key (local time zone).
 */
export function computeStreaks(entryDates: readonly string[], todayKey: string): Streaks {
  if (entryDates.length === 0) {
    return { current: 0, longest: 0, wroteToday: false };
  }

  // Unique and sorted descending (most recent first).
  const days = [...new Set(entryDates)].toSorted().toReversed();
  const wroteToday = days[0] === todayKey;

  // ── Current streak: starts today (if written) or yesterday; otherwise 0.
  let current = 0;
  const yesterdayKey = addDaysToKey(todayKey, -1);
  let cursor: string | null = null;
  if (days[0] === todayKey) {
    cursor = todayKey;
  } else if (days[0] === yesterdayKey) {
    cursor = yesterdayKey;
  }
  if (cursor !== null) {
    for (const day of days) {
      if (day === cursor) {
        current += 1;
        cursor = addDaysToKey(cursor, -1);
      } else if (day < cursor) {
        break;
      }
    }
  }

  // ── Longest streak in history.
  let longest = 0;
  let run = 0;
  let previous: string | null = null;
  for (const day of days.toReversed()) {
    run = previous !== null && diffInDays(previous, day) === 1 ? run + 1 : 1;
    if (run > longest) longest = run;
    previous = day;
  }

  return { current, longest, wroteToday };
}
